import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Tag, LogOut, FileSpreadsheet, Layers, Users, Building2, LayoutGrid, Menu, Home, KeyRound, Download, Settings, ChevronDown, Users2 } from "lucide-react";
import { useInstallApp, InstallHelpDialog } from "@/components/InstallApp";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { NotificationsBell } from "@/components/NotificationsBell";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { downloadFile } from "@/lib/api";
import { toast } from "sonner";

export const AppNav = ({ children }) => {
  const { user, logout, can } = useAuth();
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState(false);
  const pwa = useInstallApp();
  const forced = !!user?.must_change_password;
  useEffect(() => { if (forced) setPw(true); }, [forced]);
  const { pathname } = useLocation();
  const sample = async () => {
    try {
      await downloadFile("/template/sample", "USALI_Sample_Template.xlsx");
      toast.success("USALI sample template downloaded");
    } catch (e) {
      toast.error(e.message);
    }
  };
  const links = [
    { to: "/", label: "Budget Workspace", icon: Home, testid: "nav-home", show: true },
    { to: "/overview", label: "Overview", icon: LayoutGrid, testid: "nav-overview", show: true },
    { to: "/rate-shopper", label: "Rate Shopper", icon: Tag, testid: "nav-rate-shopper", show: true },
    { to: "/consolidation", label: "Group Consolidation", icon: Layers, testid: "nav-consolidation", show: can("consolidation") },
    { to: "/team", label: "Hotel Team", icon: Users2, testid: "nav-team", show: ["general_manager", "super_admin"].includes(user?.role) },
  ].filter((l) => l.show);
  const roleTag = user?.role === "super_admin" ? "admin" : user?.role === "general_manager" ? "GM" : user?.role === "department" ? user.department : null;
  const settings = [
    { to: "/admin/properties", label: "Properties", icon: Building2, testid: "nav-properties", show: can("manage_users") },
    { to: "/admin/users", label: "Users", icon: Users, testid: "nav-users", show: can("manage_users") },
  ].filter((l) => l.show);
  const inSettings = settings.some((l) => l.to === pathname);
  return (
    <header className="backdrop-blur-md bg-white/80 border-b border-slate-200/80 sticky top-0 z-50 safe-top">
      <div className="px-4 sm:px-6 lg:px-8 h-14 flex items-center gap-3 sm:gap-4">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
          <img src="/armella-logo.png" alt="Armella" className="h-[26px] w-auto" data-testid="armella-logo" />
          <span className="hidden xl:block h-6 w-px bg-slate-300" />
          <p className="hidden xl:block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold whitespace-nowrap">USALI Budget Tools</p>
        </Link>
        <div className="flex-1 min-w-0">{children}</div>
        {links.slice(1).map((l) => (
          <Link key={l.to} to={l.to} className={`hidden md:inline-flex items-center gap-1.5 text-[13px] font-medium transition-colors px-3 py-1.5 rounded-md ${pathname === l.to ? "text-emerald-800 bg-emerald-50" : "text-slate-700 hover:text-emerald-700 hover:bg-slate-100"}`} data-testid={`${l.testid}-link`}>
            <l.icon size={15} /> {l.label}
          </Link>
        ))}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className={`hidden md:inline-flex items-center gap-1.5 text-[13px] font-medium transition-colors px-3 py-1.5 rounded-md ${inSettings ? "text-emerald-800 bg-emerald-50" : "text-slate-700 hover:text-emerald-700 hover:bg-slate-100"}`} data-testid="nav-settings-menu">
              <Settings size={15} /> Settings <ChevronDown size={13} className="text-slate-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" data-testid="nav-settings-dropdown">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">Settings</DropdownMenuLabel>
            {settings.map((l) => (
              <DropdownMenuItem key={l.to} asChild>
                <Link to={l.to} data-testid={`${l.testid}-link`} className="flex items-center gap-2 cursor-pointer"><l.icon size={14} /> {l.label}</Link>
              </DropdownMenuItem>
            ))}
            {settings.length > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem onSelect={sample} data-testid="download-sample-template-button" className="flex items-center gap-2 cursor-pointer"><FileSpreadsheet size={14} /> Sample Template</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setPw(true)} data-testid="nav-change-password-item" className="flex items-center gap-2 cursor-pointer"><KeyRound size={14} /> Change password</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <span className="hidden md:block h-6 w-px bg-slate-200 mx-1" />
        <button onClick={() => setPw(true)} className="text-[13px] text-slate-600 hidden md:inline-flex items-center gap-1.5 hover:text-emerald-700 max-w-[200px] truncate" data-testid="nav-user-name" title="Change password">{user?.name}{roleTag ? <span className="ml-1 text-[10px] uppercase tracking-wider text-emerald-700 font-semibold" data-testid="nav-role-tag">{roleTag}</span> : null}<KeyRound size={13} className="text-slate-400" /></button>
        <NotificationsBell />
        {!pwa.standalone && <Button variant="ghost" size="icon" onClick={pwa.install} data-testid="install-app-button" title="Install as app" className="hidden md:inline-flex text-emerald-700">
          <Download size={16} />
        </Button>}
        <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button" title="Sign out" className="hidden md:inline-flex">
          <LogOut size={16} />
        </Button>
        <Button variant="ghost" size="icon" onClick={() => setOpen(true)} data-testid="mobile-menu-button" title="Menu" className="md:hidden">
          <Menu size={22} />
        </Button>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-[82vw] max-w-sm p-0 flex flex-col" data-testid="mobile-menu">
          <SheetHeader className="px-5 pt-6 pb-4 border-b border-slate-200 text-left">
            <img src="/armella-logo.png" alt="Armella" className="h-[27px] w-auto max-w-[180px] self-start object-contain" data-testid="mobile-menu-logo" />
            <SheetTitle className="font-heading text-base mt-2">USALI Budget Tools</SheetTitle>
            <p className="text-xs text-slate-500" data-testid="mobile-menu-user">{user?.name}{roleTag ? ` · ${roleTag}` : ""}</p>
          </SheetHeader>
          <nav className="flex-1 overflow-y-auto py-2">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-${l.testid}-link`}
                className={`flex items-center gap-3 px-5 py-3.5 text-base font-medium border-l-4 ${pathname === l.to ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-transparent text-slate-800 hover:bg-slate-50"}`}>
                <l.icon size={18} /> {l.label}
              </Link>
            ))}
            <p className="px-5 pt-4 pb-1 text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold inline-flex items-center gap-1.5" data-testid="mobile-settings-heading"><Settings size={11} /> Settings</p>
            {settings.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-${l.testid}-link`}
                className={`flex items-center gap-3 px-5 py-3.5 text-base font-medium border-l-4 ${pathname === l.to ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-transparent text-slate-800 hover:bg-slate-50"}`}>
                <l.icon size={18} /> {l.label}
              </Link>
            ))}
            <button onClick={() => { setOpen(false); sample(); }} data-testid="mobile-sample-template-button" className="w-full flex items-center gap-3 px-5 py-3.5 text-base font-medium text-slate-800 hover:bg-slate-50 border-l-4 border-transparent">
              <FileSpreadsheet size={18} /> Sample Template
            </button>
            <button onClick={() => { setOpen(false); setPw(true); }} data-testid="mobile-change-password-button" className="w-full flex items-center gap-3 px-5 py-3.5 text-base font-medium text-slate-800 hover:bg-slate-50 border-l-4 border-transparent">
              <KeyRound size={18} /> Change password
            </button>
            {!pwa.standalone && <button onClick={() => { setOpen(false); pwa.install(); }} data-testid="mobile-install-app-button" className="w-full flex items-center gap-3 px-5 py-3.5 text-base font-medium text-emerald-800 bg-emerald-50/60 hover:bg-emerald-50 border-l-4 border-emerald-600">
              <Download size={18} /> Install as app
            </button>}
          </nav>
          <div className="border-t border-slate-200 p-4">
            <Button variant="outline" onClick={() => { setOpen(false); logout(); }} data-testid="mobile-logout-button" className="w-full justify-start">
              <LogOut size={16} className="mr-2" /> Sign out
            </Button>
          </div>
        </SheetContent>
      </Sheet>
      <ChangePasswordDialog open={pw} onOpenChange={setPw} forced={forced} />
      <InstallHelpDialog open={pwa.help} onOpenChange={pwa.setHelp} />
    </header>
  );
};
